import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Heading } from '../../components/Heading';
import { Link } from '../../components/Link';
import { MaintenanceState } from '../../components/MaintenanceState/MaintenanceState';
import { PowerBiReport } from '../../components/PowerBiReport';
import { routes } from '../../controllers/ContentController/Routes';
import { useCurrentUser, usePrincipals } from '../../hooks/queries';
import { getCurrentPrincipal } from '../../lib/currentPrincipal';
import { PrimaryFeature, SecondaryFeature } from '../../utils/types';
import { DashboardMap } from './Map/DashboardMap';
import { useNavigationContext } from '../../context/NavigationProvider';

export const Dashboard: React.FC = () => {
  const { primaryFeature } = useNavigationContext();
  const { user, update } = useCurrentUser();
  const { data: principals } = usePrincipals();
  const { currentPrincipal } = getCurrentPrincipal(principals);
  const reportingRoute = routes(currentPrincipal.slug).find(
    PrimaryFeature.REPORTING,
  );

  const { t } = useTranslation();

  useEffect(() => {
    if (!user || user?.signedIn) return;
    update({ signedIn: true });
  }, [user, update]);

  return (
    <div className="w-full">
      <div className="bg-grey-100 w-full px-4 md:px-6">
        <div className="max-w-xl-content mx-auto">
          <div className="mb-4 pt-6 md:pt-7" data-test="page-header">
            <div className="flex flex-col items-baseline justify-between md:flex-row md:space-x-2">
              <Heading
                text={t('features:dashboard:heading')}
                margin={0}
                level="h1"
              />
            </div>
            <div className="text-grey-700 mt-1">
              {user.name
                ? t('features:dashboard:welcome', {
                    name: user?.name,
                  })
                : t('features:dashboard:welcome_no_name')}
            </div>
          </div>
          <div>
            {primaryFeature?.secondaryFeatures?.find(
              ({ id }) => id === SecondaryFeature.DASHBOARD_KEY_SALES_NUMBERS,
            ) &&
              currentPrincipal && (
                <>
                  <Heading
                    level="h2"
                    text={t('features:reports:key_sales_numbers')}
                  />
                  {primaryFeature?.secondaryFeatures.find(
                    ({ id }) =>
                      id === SecondaryFeature.DASHBOARD_KEY_SALES_NUMBERS,
                  )?.maintenanceMode ? (
                    <MaintenanceState />
                  ) : (
                    <div className="flex flex-col">
                      <PowerBiReport />
                      <div className="self-end pb-4 pr-3">
                        <Link
                          type="router"
                          icon="ArrowRight"
                          iconPosition="right"
                          inline={true}
                          variant="compact"
                          href={reportingRoute.url}
                          label={t(
                            'features:dashboard:linkLabelReport',
                          ).toUpperCase()}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
          </div>
        </div>
      </div>
      <div className="w-full bg-white px-4 md:px-6">
        <div className="max-w-xl-content mx-auto pt-8">
          <DashboardMap selectedPrincipal={currentPrincipal} />
        </div>
      </div>
    </div>
  );
};
